import {
  Checkbox as ChakraCheckbox,
  useToken,
  forwardRef,
} from '@chakra-ui/react-v2';
import type {
  CheckboxProps as ChakraCheckboxProps,
  SystemStyleObject,
} from '@chakra-ui/react-v2';
import React from 'react';
import type { LiteralUnion } from 'type-fest';

import type { ThemeColor } from '@frontend/legacy-components';

import {
  deriveHoverShiftPercent,
  isLightAccent,
} from './getContrastingCheckmarkColor';

export interface CheckboxProps extends ChakraCheckboxProps {
  /**
   * Recolors the checked/indeterminate box (background + border). Accepts a
   * theme color token (e.g. `'interaction.main'`) or a raw hex code. The
   * checkmark inside flips between black and white for best contrast, and the
   * hover state derives a perceptually-uniform shade from the accent. Omit for
   * the default styling. Disabled/readOnly/invalid states are unaffected.
   */
  accentColor?: LiteralUnion<ThemeColor, string>;
}

const ACCENT_TUNE_AMOUNT = 1.0;
const ACCENT_TUNE_MIDPOINT = 0.5;

/**
 * Build the `sx` overrides that recolor the checked/indeterminate box and
 * derive a hover shade — all scoped so they only apply to the enabled, valid
 * states (disabled/readOnly/invalid keep their base look). `sx` lands on the
 * root (`label.chakra-checkbox`), so these are descendant selectors targeting
 * the control part.
 *
 * Mid-tone accents are first contrast-amplified in OKLCH (`L` pushed away from
 * the midpoint), then the checkmark color is delegated to the browser via
 * `contrast-color()` on that tuned fill (Baseline April 2026: Chrome 147,
 * Firefox 146, Safari 26).
 */
const buildAccentSx = (
  accent: string,
  interactionMain: string,
  interactionHover: string,
): SystemStyleObject => {
  const shiftPercent = deriveHoverShiftPercent(
    interactionMain,
    interactionHover,
  );
  const hoverEndpoint = isLightAccent(accent) ? 'black' : 'white';
  // Tuned fill is the source of truth for checked bg / mark / hover; raw accent
  // only seeds the relative-color formula.
  const tuned = 'var(--pl-checkbox-tuned-accent)';
  const hoverAccent = `color-mix(in oklab, ${tuned} ${
    100 - shiftPercent
  }%, ${hoverEndpoint})`;
  // The border is always a shade darker than its fill so the box keeps a defined
  // edge — one theme-derived shift step toward black, in the same perceptual space.
  const darken = (fill: string): string =>
    `color-mix(in oklab, ${fill} ${100 - shiftPercent}%, black)`;

  return {
    '--pl-checkbox-accent': accent,
    '--pl-checkbox-tune-amount': String(ACCENT_TUNE_AMOUNT),
    '--pl-checkbox-tune-midpoint': String(ACCENT_TUNE_MIDPOINT),
    // L_new = L + (L - L_mid) * tune — light accents lighten, dark accents darken.
    '--pl-checkbox-tuned-accent': `oklch(from var(--pl-checkbox-accent) calc(l + (l - var(--pl-checkbox-tune-midpoint)) * var(--pl-checkbox-tune-amount)) c h)`,
    // Checked + indeterminate box. Exclude disabled/readOnly/invalid so those
    // states keep their base styling.
    [`.chakra-checkbox__control[data-checked]:not([data-disabled]):not([data-readonly]):not([data-invalid]),
      .chakra-checkbox__control[data-indeterminate]:not([data-disabled]):not([data-readonly]):not([data-invalid])`]:
      {
        bg: tuned,
        borderColor: darken(tuned),
        color: 'contrast-color(var(--pl-checkbox-tuned-accent))',
      },
    // Hover replicates the base container `_hover` qualifier chain so it matches
    // that rule's specificity — otherwise the box snaps back to interaction.hover.
    [`&:hover .chakra-checkbox__control:not([data-readonly]):not([data-disabled]):not([data-invalid])[data-checked],
      &:hover .chakra-checkbox__control:not([data-readonly]):not([data-disabled]):not([data-invalid])[data-indeterminate]`]:
      {
        bg: hoverAccent,
        borderColor: darken(hoverAccent),
      },
    // Unchecked box outline picks up the (darkened) tuned accent too (bg stays page.surface).
    [`.chakra-checkbox__control:not([data-checked]):not([data-indeterminate]):not([data-disabled]):not([data-readonly]):not([data-invalid])`]:
      {
        borderColor: darken(tuned),
      },
    // Unchecked hover — same specificity-matching approach as the checked hover.
    [`&:hover .chakra-checkbox__control:not([data-readonly]):not([data-disabled]):not([data-invalid]):not([data-checked]):not([data-indeterminate])`]:
      {
        borderColor: darken(hoverAccent),
      },
  };
};

export const Checkbox: React.FC<CheckboxProps> = forwardRef<
  CheckboxProps,
  'input'
>(({ accentColor, sx, ...rest }, ref) => {
  // Hooks must run unconditionally (Rules of Hooks). useToken resolves theme
  // tokens and passes raw hex/CSS strings through unchanged; the 'transparent'
  // fallback keeps the call stable when accentColor is undefined.
  const [resolvedAccent, interactionMain, interactionHover] = useToken(
    'colors',
    [accentColor ?? 'transparent', 'interaction.main', 'interaction.hover'],
  );

  // accentSx first so a caller-supplied `sx` always wins on conflict.
  const mergedSx = accentColor
    ? {
        ...buildAccentSx(resolvedAccent, interactionMain, interactionHover),
        ...sx,
      }
    : sx;

  return <ChakraCheckbox ref={ref} sx={mergedSx} {...rest} />;
});
